export interface DragHandler<T> {
  isInDragArea: (e: MouseEvent) => { isInDragArea: false } | { isInDragArea: true; dragPayload: T };
  onDragStart?: (e: PointerEvent, dragPayload: T) => void;
  onDragMove?: (e: PointerEvent, dragPayload: T) => void;
  onDragComplete?: (e: PointerEvent, dragPayload: T) => void;
  onDragCancel?: (dragPayload: T) => void;
  onDragAreaMouseEnter?: (e: PointerEvent) => void;
  onDragAreaMouseLeave?: (e: PointerEvent) => void;
  onDragAreaDoubleClick?: (e: MouseEvent) => void;
  dragStartDistancePx?: number;
}

interface DragHandlerWithPayload {
  handler: DragHandler<unknown>;
  dragPayload: unknown;
}

type DragOperation = DragHandlerWithPayload & {
  pointerdownX: number;
  pointerdownY: number;
  pointerId: number;
};

export default class DragManager {
  private readonly handlersByPriority: { handler: DragHandler<unknown>; priority: number }[] = [];

  // The pointer is clicked at the moment, we are in a drag operation.
  private activeDragOperation: DragOperation | undefined = undefined;

  // The pointer was moved while in a drag operation. If there's no pointer movement we bail out of
  // the drag operation and treat it as a normal click. This allows clicking on elements that
  // overlap the drag area.
  private didMovePointerWhileDragging = false;

  // Animation frame for the pointer move update
  private pointermoveAnimationFrame: number | undefined;

  // The pointer was in the drag area on the previous pointer move event. Used to detect pointer
  // moving out of the drag area while not dragging.
  private wasInDragAreaForHandler: DragHandler<unknown> | undefined;

  // We can't call preventDefault for a pointerup event, see https://stackoverflow.com/a/19436323.
  // Instead we remember the time of the pointerup and prevent the click in a click event handler if
  // it happens immediately afterwards.
  private preventClickInNextTimeInterval: number | undefined;

  // To be able to listen to double clicks we can't act immediately when a click happens. Instead we
  // delay the click handler and wait for a 2nd click to happen in the meantime.
  private delayedClickTimeout: number | undefined;

  // Measure double click time. Sadly we can't get the users set double click time directly, we
  // can only determine indirectly by checking when the browser considers a click related to a
  // previous click (via the event.details property). So we start with a reasonable default for
  // the double click time and then increase it according to measurement.
  private doubleClickDelay = 300;
  private lastClickTimestamp: number | undefined;

  constructor(private draggingClass: string) {}

  public init() {
    document.addEventListener('pointerdown', this.handlePointerdown);
    document.addEventListener('pointermove', this.handlePointermove);
    document.addEventListener('pointerup', this.handlePointerup);
    document.addEventListener('pointercancel', this.handlePointercancel);
    // To hide the click event from react we need to prevent if before it ever reaches the react
    // root (else the react event emulation will take over). We register the event listener in the
    // capturing phase to get the event before it propagates down.
    document.addEventListener('click', this.handleClick, true);
    document.addEventListener('dragstart', this.handleDragstart);
    document.addEventListener('touchmove', this.handleTouchmove);
  }

  public close() {
    document.removeEventListener('pointerdown', this.handlePointerdown);
    document.removeEventListener('pointermove', this.handlePointermove);
    document.removeEventListener('pointerup', this.handlePointerup);
    document.removeEventListener('pointercancel', this.handlePointercancel);
    document.removeEventListener('click', this.handleClick, true);
    document.removeEventListener('dragstart', this.handleDragstart);
    document.removeEventListener('touchmove', this.handleTouchmove);
  }

  public addHandler<T>(handler: DragHandler<T>, priority: number) {
    this.handlersByPriority.push({ handler: handler as DragHandler<unknown>, priority });
    this.handlersByPriority.sort((a, b) => b.priority - a.priority);
  }

  public removeHandler<T>(handler: DragHandler<T>) {
    const index = this.handlersByPriority.findIndex((hbp) => hbp.handler === handler);
    if (index !== -1) {
      if (handler === this.activeDragOperation?.handler) {
        this.activeDragOperation = undefined;
      }
      this.handlersByPriority.splice(index, 1);
    }
  }

  public cancelDrag() {
    if (!this.activeDragOperation) {
      return;
    }
    this.activeDragOperation.handler.onDragCancel?.(this.activeDragOperation.dragPayload);
    this.activeDragOperation = undefined;
  }

  public isInDragFor<T>(handler: DragHandler<T>) {
    return this.activeDragOperation?.handler === handler;
  }

  private getDragHandlerWithPayload(e: MouseEvent): DragHandlerWithPayload | undefined {
    for (const { handler } of this.handlersByPriority) {
      const result = handler.isInDragArea(e);
      if (result.isInDragArea) {
        return { handler, dragPayload: result.dragPayload };
      }
    }
    return undefined;
  }

  private readonly handlePointerdown = (e: PointerEvent) => {
    if (this.activeDragOperation) {
      return;
    }
    const handlerWithPayload = this.getDragHandlerWithPayload(e);
    if (!handlerWithPayload) {
      return;
    }
    this.activeDragOperation = {
      ...handlerWithPayload,
      pointerdownX: e.clientX,
      pointerdownY: e.clientY,
      pointerId: e.pointerId,
    };
    document.documentElement.classList.add(this.draggingClass);
  };

  private readonly handlePointermove = (e: PointerEvent) => {
    if (this.pointermoveAnimationFrame) {
      window.cancelAnimationFrame(this.pointermoveAnimationFrame);
    }
    if (this.activeDragOperation && this.activeDragOperation.pointerId === e.pointerId) {
      e.preventDefault();
    }
    this.pointermoveAnimationFrame = window.requestAnimationFrame(() => {
      if (this.activeDragOperation && this.activeDragOperation.pointerId === e.pointerId) {
        if (!this.didMovePointerWhileDragging) {
          const startDistanceSquared =
            (e.clientX - this.activeDragOperation.pointerdownX) ** 2 +
            (e.clientY - this.activeDragOperation.pointerdownY) ** 2;
          if (
            this.activeDragOperation.handler.dragStartDistancePx === undefined ||
            startDistanceSquared > this.activeDragOperation.handler.dragStartDistancePx ** 2
          ) {
            this.didMovePointerWhileDragging = true;
            this.activeDragOperation.handler.onDragStart?.(e, this.activeDragOperation.dragPayload);
          }
        } else {
          this.activeDragOperation.handler.onDragMove?.(e, this.activeDragOperation.dragPayload);
        }
        return;
      }

      const handlerWithPayload = this.getDragHandlerWithPayload(e);
      if (
        this.wasInDragAreaForHandler &&
        this.wasInDragAreaForHandler !== handlerWithPayload?.handler
      ) {
        this.wasInDragAreaForHandler.onDragAreaMouseLeave?.(e);
        this.wasInDragAreaForHandler = undefined;
      }

      if (handlerWithPayload && this.wasInDragAreaForHandler !== handlerWithPayload.handler) {
        handlerWithPayload.handler.onDragAreaMouseEnter?.(e);
        this.wasInDragAreaForHandler = handlerWithPayload.handler;
      }
    });
  };

  private readonly handlePointerup = (e: PointerEvent) => {
    if (this.activeDragOperation && this.activeDragOperation.pointerId === e.pointerId) {
      if (this.didMovePointerWhileDragging) {
        this.activeDragOperation.handler.onDragComplete?.(e, this.activeDragOperation.dragPayload);
        this.preventClickInNextTimeInterval = Date.now();
      }
      this.activeDragOperation = undefined;
      this.didMovePointerWhileDragging = false;
      document.documentElement.classList.remove(this.draggingClass);
    }
  };

  private readonly handlePointercancel = (e: PointerEvent) => {
    if (this.activeDragOperation && this.activeDragOperation.pointerId === e.pointerId) {
      if (this.didMovePointerWhileDragging) {
        this.activeDragOperation.handler.onDragCancel?.(this.activeDragOperation.dragPayload);
        this.preventClickInNextTimeInterval = Date.now();
      }
      this.activeDragOperation = undefined;
      this.didMovePointerWhileDragging = false;
      document.documentElement.classList.remove(this.draggingClass);
    }
  };

  private readonly handleClick = (e: MouseEvent) => {
    // Measure double click delay, see this.doubleClickDelay
    if (e.detail === 2 && this.lastClickTimestamp) {
      const delay = Date.now() - this.lastClickTimestamp;
      this.doubleClickDelay = Math.max(this.doubleClickDelay, delay);
    }
    this.lastClickTimestamp = Date.now();

    // Prevent click event from going through if it happens soon after a pointerup event.
    if (
      this.preventClickInNextTimeInterval &&
      Date.now() - this.preventClickInNextTimeInterval < 50
    ) {
      e.preventDefault();
      return;
    }

    // Click on drag area. If there was a previous click, emit double click event. Else delay click
    // to allow for a 2nd click to occur within the interval.
    const handlerWithPayload = this.getDragHandlerWithPayload(e);
    if (handlerWithPayload && handlerWithPayload.handler.onDragAreaDoubleClick) {
      e.preventDefault();
      if (e.detail > 1) {
        window.clearTimeout(this.delayedClickTimeout);
        this.delayedClickTimeout = undefined;
        handlerWithPayload.handler.onDragAreaDoubleClick(e);
      } else {
        const target = getHTMLElementTarget(e);
        if (target) {
          this.delayedClickTimeout = window.setTimeout(() => {
            target.click();
          }, this.doubleClickDelay);
        }
      }
    }
  };

  private readonly handleDragstart = (e: DragEvent): void => {
    if (this.activeDragOperation) {
      e.preventDefault();
    }
  };

  private readonly handleTouchmove = (e: TouchEvent): void => {
    if (this.activeDragOperation) {
      e.preventDefault();
    }
  };
}

function getHTMLElementTarget(e: MouseEvent) {
  if (e.target instanceof HTMLElement) {
    return e.target;
  }
  if (e.target instanceof Node) {
    let cur: Node | null = e.target;
    do {
      cur = cur.parentNode;
      if (cur instanceof HTMLElement) {
        return cur;
      }
    } while (cur);
  }
  return undefined;
}
